
import {of as observableOf,  Observable } from 'rxjs';

import {catchError} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AddContact, UpdateContact } from '@routes/addressbook/models/addressbook-model';
import { DeleteContacts, EnableContacts, IContactByUser, IContactDetails, IContactExportDetails, IContacts,
    IContactsForDropdown } from '@shared/services/api/legacy/contacts-service/contacts-model';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/legacy/response';

@Injectable()
export class ContactsService {
    constructor(private http: HttpClient) { }

    private static get URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${ContactsService.ENDPOINT}`; }
    private static readonly ENDPOINT = '/contacts';

    public getContact(id: string): Observable<SuccessResponse<IContactDetails> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IContactDetails>>(ContactsService.URL + '/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getContactByUser(id: string): Observable<SuccessResponse<IContactByUser> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IContactByUser>>(ContactsService.URL + '/users/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getContacts(): Observable<SuccessResponse<IContacts> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IContacts>>(ContactsService.URL).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getFilteredContacts(data): Observable<SuccessResponse<IContacts> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IContacts>>(ContactsService.URL + '/index', data).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getContactsForInvitations(eventId: string, data): Observable<SuccessResponse<IContacts> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IContacts>>(ContactsService.URL + '/invitations/' + eventId, data).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getContactsForCampaigns(campaignId: string, data): Observable<SuccessResponse<IContacts> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IContacts>>(ContactsService.URL + '/recipients/' + campaignId, data).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getContactsForDropdown(): Observable<SuccessResponse<IContactsForDropdown> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IContactsForDropdown>>(ContactsService.URL + '/dropdown').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addContact(contact: AddContact): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(ContactsService.URL, contact).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateContact(contact: UpdateContact): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(ContactsService.URL, contact).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteContact(id: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(ContactsService.URL + '/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteContacts(ids: string[]): Observable<SuccessResponse<boolean> | ErrorResponse> {
        const model = new DeleteContacts(ids);
        return this.http
            .post<SuccessResponse<boolean>>(ContactsService.URL + '/deletebatch', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public enableContact(id: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(ContactsService.URL + '/enable/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public enableContacts(ids: string[]): Observable<SuccessResponse<boolean> | ErrorResponse> {
        const model = new EnableContacts(ids);
        return this.http
            .post<SuccessResponse<boolean>>(ContactsService.URL + '/enablebatch', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public anonimizeContact(id: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(ContactsService.URL + '/anonimize/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public anonimizeContacts(ids: string[]): Observable<SuccessResponse<boolean> | ErrorResponse> {
        const model = new DeleteContacts(ids);
        return this.http
            .post<SuccessResponse<boolean>>(ContactsService.URL + '/anonimizebatch', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getContactExport(data): Observable<SuccessResponse<IContactExportDetails> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IContactExportDetails>>(ContactsService.URL + '/export', data).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }
}
